import { Icon, Badge } from '@/components/atoms';
import { useBatchTransactions } from '@/hooks/useErpApi';

interface BatchDetailProps {
  batch: any; // Full batch object from API response
}

export const BatchDetail: React.FC<BatchDetailProps> = ({ batch }) => {
  const { data: transactions, isLoading, error } = useBatchTransactions(batch.id);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
          <p className="text-sm text-neutral-600">Loading transaction history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-error-50 border border-error-200 rounded-lg p-4">
        <div className="flex items-center gap-2">
          <Icon name="CircleAlert" size={20} className="text-error-600" />
          <p className="text-error-700 text-sm">Failed to load transaction history</p>
        </div>
      </div>
    );
  }

  const getTransactionIcon = (
    type: string
  ): 'CircleArrowDown' | 'CircleArrowUp' | 'Pencil' | 'RotateCcw' | 'Activity' => {
    switch (type) {
      case 'purchase':
      case 'grn':
        return 'CircleArrowDown';
      case 'sale':
        return 'CircleArrowUp';
      case 'adjustment':
        return 'Pencil';
      case 'return':
        return 'RotateCcw';
      default:
        return 'Activity';
    }
  };

  const getTransactionColor = (change: number) => {
    if (change > 0) return 'text-success-600';
    if (change < 0) return 'text-error-600';
    return 'text-neutral-600';
  };

  // Extract data from batch object
  const warehouse = batch.warehouse || {};
  const product = batch.product || {};
  const variant = batch.variant || {};
  const quantityDetails = batch.quantity_details || {};
  const batchInfo = batch.batch_info || {};
  const taxConfig = batch.tax_config || {};

  return (
    <div className="space-y-6">
      {/* Batch Information */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pb-4 border-b border-neutral-200">
        <div>
          <p className="text-xs text-neutral-500 mb-1">Warehouse</p>
          <p className="text-sm font-medium text-neutral-900">{warehouse.name || 'N/A'}</p>
          <p className="text-xs text-neutral-500 font-mono">{warehouse.id || ''}</p>
        </div>
        <div>
          <p className="text-xs text-neutral-500 mb-1">Product</p>
          <p className="text-sm font-medium text-neutral-900">{product.name || 'N/A'}</p>
          <p className="text-xs text-neutral-500 font-mono">{product.id || ''}</p>
        </div>
        <div>
          <p className="text-xs text-neutral-500 mb-1">Variant</p>
          <p className="text-sm font-medium text-neutral-900">{variant.variant_name || variant.id || 'N/A'}</p>
          <p className="text-xs text-neutral-500">{variant.brand_name || ''}</p>
        </div>
        <div>
          <p className="text-xs text-neutral-500 mb-1">SKU</p>
          <p className="text-sm font-medium text-neutral-900 font-mono">{variant.sku || 'N/A'}</p>
        </div>
      </div>

      {/* Quantity Details */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pb-4 border-b border-neutral-200">
        <div>
          <p className="text-xs text-neutral-500 mb-1">Total Quantity</p>
          <p className="text-sm font-semibold text-neutral-900">{quantityDetails.total_quantity || 0}</p>
        </div>
        <div>
          <p className="text-xs text-neutral-500 mb-1">Available Quantity</p>
          <p className="text-sm font-semibold text-success-600">{quantityDetails.available_quantity || 0}</p>
        </div>
        <div>
          <p className="text-xs text-neutral-500 mb-1">Reserved Quantity</p>
          <p className="text-sm font-semibold text-warning-600">{quantityDetails.reserved_quantity || 0}</p>
        </div>
        <div>
          <p className="text-xs text-neutral-500 mb-1">Sold Quantity</p>
          <p className="text-sm font-semibold text-neutral-900">{quantityDetails.sold_quantity || 0}</p>
        </div>
      </div>

      {/* Batch Info & Tax Config */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pb-4 border-b border-neutral-200">
        <div>
          <p className="text-xs text-neutral-500 mb-1">Expiry Date</p>
          <p className="text-sm font-medium text-neutral-900">
            {batchInfo.expiry_date ? new Date(batchInfo.expiry_date).toLocaleDateString() : 'N/A'}
          </p>
          {batchInfo.days_until_expiry !== undefined && (
            <p className="text-xs text-neutral-500">{batchInfo.days_until_expiry} days remaining</p>
          )}
        </div>
        <div>
          <p className="text-xs text-neutral-500 mb-1">Expiry Status</p>
          <Badge variant={batchInfo.expiry_status === 'good' ? 'success' : batchInfo.expiry_status === 'warning' ? 'warning' : 'error'}>
            {batchInfo.expiry_status || 'N/A'}
          </Badge>
        </div>
        <div>
          <p className="text-xs text-neutral-500 mb-1">GST Rate</p>
          <p className="text-sm font-medium text-neutral-900">{taxConfig.gst_rate || 0}%</p>
          <p className="text-xs text-neutral-500">
            CGST: {taxConfig.cgst_rate || 0}% | SGST: {taxConfig.sgst_rate || 0}%
          </p>
        </div>
        <div>
          <p className="text-xs text-neutral-500 mb-1">HSN Code</p>
          <p className="text-sm font-medium text-neutral-900 font-mono">{taxConfig.hsn_code || 'N/A'}</p>
        </div>
      </div>

      {/* Transaction History */}
      <div className="flex items-center justify-between">
        <h4 className="text-lg font-semibold text-neutral-900">Transaction History</h4>
        <Badge variant="info">{transactions?.length || 0} Transactions</Badge>
      </div>

      {!transactions || transactions.length === 0 ? (
        <div className="text-center py-8">
          <Icon name="FileText" size={40} className="text-neutral-300 mx-auto mb-2" />
          <p className="text-neutral-500">No transaction history available</p>
        </div>
      ) : (
        <div className="space-y-3">
          {transactions.map((transaction) => (
            <div
              key={transaction.id}
              className="bg-white border border-neutral-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="bg-primary-50 p-2 rounded-lg mt-0.5">
                    <Icon
                      name={getTransactionIcon(transaction.transaction_type)}
                      size={20}
                      className="text-primary-600"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h5 className="font-medium text-neutral-900 capitalize">
                        {transaction.transaction_type}
                      </h5>
                      <Badge
                        variant={
                          transaction.quantity_change > 0
                            ? 'success'
                            : transaction.quantity_change < 0
                              ? 'error'
                              : 'info'
                        }
                      >
                        {transaction.quantity_change > 0 ? '+' : ''}
                        {transaction.quantity_change}
                      </Badge>
                    </div>
                    <div className="mt-1 space-y-1">
                      <p className="text-sm text-neutral-600">
                        <span className="font-medium">Transaction ID:</span>{' '}
                        <span className="font-mono text-xs">{transaction.id}</span>
                      </p>
                      {transaction.related_entity_id && (
                        <p className="text-sm text-neutral-600">
                          <span className="font-medium">Related Entity:</span>{' '}
                          <span className="font-mono text-xs">{transaction.related_entity_id}</span>
                        </p>
                      )}
                      {transaction.note && (
                        <p className="text-sm text-neutral-600">
                          <span className="font-medium">Note:</span> {transaction.note}
                        </p>
                      )}
                      <p className="text-sm text-neutral-500">
                        <span className="font-medium">Performed by:</span>{' '}
                        {transaction.performed_by}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-neutral-500">
                    {new Date(transaction.occurred_at).toLocaleDateString()}
                  </p>
                  <p className="text-xs text-neutral-500">
                    {new Date(transaction.occurred_at).toLocaleTimeString()}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Transaction Flow Visualization */}
      {transactions && transactions.length > 0 && (
        <div className="bg-gradient-to-br from-info-50 to-info-100 rounded-lg p-4 mt-4">
          <h5 className="font-medium text-info-900 mb-3 flex items-center gap-2">
            <Icon name="GitBranch" size={16} />
            Transaction Flow
          </h5>
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            {transactions
              .slice()
              .reverse()
              .map((txn, idx) => (
                <div key={txn.id} className="flex items-center gap-2">
                  <div className="bg-white rounded-lg px-3 py-2 shadow-sm min-w-fit">
                    <p className="text-xs font-semibold text-info-900 capitalize">
                      {txn.transaction_type}
                    </p>
                    <p className={`text-sm font-bold ${getTransactionColor(txn.quantity_change)}`}>
                      {txn.quantity_change > 0 ? '+' : ''}
                      {txn.quantity_change}
                    </p>
                  </div>
                  {idx < transactions.length - 1 && (
                    <Icon name="ChevronRight" size={16} className="text-info-400" />
                  )}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};
